<template>
  <BaseDialog
    title="欄位選項"
    width="650px"
    :btn1-disabled="isOverLimit"
    v-bind="$attrs"
    @close="$emit('close')"
    @confirm="handleConfirm"
  >
    <FlexLayout col style="margin-bottom: 40px;">
      <!-- 警告提示 -->
      <div
        v-if="showControl.hint.optionsOverflow"
      >
        {{ displayText.hint.optionsOverflow }}
      </div>

      <BaseElForm ref="formRef" :model="formData" :rules="formRules" label-position="top">
        <FlexLayout col :gap="20">
          <!-- 父子選項設定 -->
          <div
            v-for="(group, idx) in formData.groups"
            :key="idx"
          >
            <FlexLayout :gap="8">
              <FlexLayout col>
                <BaseElFormItem
                  :prop="`groups.${idx}.parentValue`"
                  label="主欄位選項"
                  class="mb-4"
                >
                  <BaseElInput
                    v-model="group.parentValue"
                    placeholder="請輸入主欄位選項"
                  />
                </BaseElFormItem>

                <BaseElFormItem
                  :prop="`groups.${idx}.childOptions`"
                  label="子欄位選項"
                  class="mb-4"
                >
                  <BaseElSelect
                    v-model="group.childOptions"
                    style="width: 100%"
                    multiple
                    filterable
                    allow-create
                    :multiple-limit="200"
                    placeholder="請輸入子欄位選項"
                  >
                    <BaseElSelectOption
                      v-for="option in group.childOptions"
                      :key="option"
                      :label="option"
                      :value="option"
                    />
                  </BaseElSelect>
                </BaseElFormItem>
              </FlexLayout>

              <!-- 刪除按鈕 -->
              <div
                v-if="formData.groups.length > 1 && idx > 0"
                class="h-[fit-content] cursor-pointer"
                @click="removeGroup(idx)"
              >
                <SVGIcon
                  icon="common-trash"
                  fill="var(--gray-60)"
                />
              </div>
            </FlexLayout>

            <BaseDivider />
          </div>

          <!-- 新增欄位按鈕 -->
          <div class="flex justify-center">
            <BaseElButton
              plain
              :disabled="isOverLimit"
              @click="addGroup"
            >
              新增欄位 +
            </BaseElButton>
          </div>

          <!-- 統計資訊 -->
          <div class="mt-4 text-sm text-gray-600 text-center">
            目前子選項總數：{{ totalChildOptions }}/1000
          </div>
        </FlexLayout>
      </BaseElForm>
    </FlexLayout>
  </BaseDialog>
</template>

<script>
import { defineComponent, computed, watch } from 'vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import FlexLayout from '@/components/FlexLayout.vue'
import BaseDivider from '@/components/BaseDivider.vue'
import { useBaseForm } from '@/use/useForm'

export default defineComponent({
  name: 'EditFieldOptionsModal',
  components: {
    BaseDialog,
    FlexLayout,
    BaseDivider,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'update:modelValue'],
  setup (props, { emit }) {
    const { formData, formRef, initFormData, checkForm } = useBaseForm()

    // 初始化表單資料
    const initializeFormData = () => {
      if (props.value && props.value.length > 0) {
        return {
          groups: props.value.map(item => ({
            parentValue: item.parentValue || '',
            childOptions: [...(item.childOptions || [])],
          })),
        }
      }
      return {
        groups: [
          {
            parentValue: '',
            childOptions: [],
          },
        ],
      }
    }

    // 初始化表單
    initFormData(initializeFormData())

    // 監聽 props 變化
    watch(() => props.value, () => {
      initFormData(initializeFormData())
    }, { deep: true })

    // 計算總子選項數量
    const totalChildOptions = computed(() => {
      return formData.groups.reduce((total, group) => {
        return total + (group.childOptions?.length || 0)
      }, 0)
    })

    // 是否超過限制
    const isOverLimit = computed(() => totalChildOptions.value > 1000)

    // 顯示控制
    const showControl = computed(() => ({
      hint: {
        optionsOverflow: isOverLimit.value,
      },
    }))

    // 顯示文字
    const displayText = {
      hint: {
        optionsOverflow: '您已超過欄位選項 1000 筆總數上限 ，請刪除選項再進行新增。',
      },
    }

    // 表單驗證規則
    const formRules = computed(() => {
      const rules = {}
      formData.groups.forEach((_, idx) => {
        rules[`groups.${idx}.parentValue`] = [
          { required: true, message: '請輸入主欄位選項', trigger: 'blur' },
        ]
        rules[`groups.${idx}.childOptions`] = [
          {
            required: true,
            message: '請至少輸入一個子欄位選項',
            trigger: 'change',
            validator: (rule, value, callback) => {
              if (!value || value.length === 0) {
                callback(new Error('請至少輸入一個子欄位選項'))
              } else {
                callback()
              }
            },
          },
        ]
      })
      return rules
    })

    // 新增群組
    const addGroup = () => {
      if (isOverLimit.value) return

      formData.groups.push({
        parentValue: '',
        childOptions: [],
      })
    }

    // 移除群組
    const removeGroup = (index) => {
      if (formData.groups.length > 1) {
        formData.groups.splice(index, 1)
      }
    }

    // 確認提交
    const handleConfirm = async () => {
      // 使用 useBaseForm 的 checkForm 方法進行驗證
      const pass = await checkForm(formRef.value)
      if (!pass) return

      // 檢查是否有至少 2 個子選項
      if (totalChildOptions.value < 2) {
        window.$message?.error('選項至少需要 2 項')
        return
      }

      // 檢查是否超過限制
      if (isOverLimit.value) {
        window.$message?.error('子選項總數不能超過 1000 項')
        return
      }

      // 過濾空的群組並提交
      const validGroups = formData.groups.filter(group =>
        group.parentValue.trim() && group.childOptions.length > 0,
      )

      emit('confirm', validGroups)
      emit('close')
    }

    return {
      formRef,
      formData,
      formRules,
      totalChildOptions,
      isOverLimit,
      showControl,
      displayText,
      addGroup,
      removeGroup,
      handleConfirm,
    }
  },
})
</script>

<style scoped lang="scss">
::v-deep .el-select {
  height: auto;
}

::v-deep .el-select .el-input {
  height: 100%;
}

::v-deep .el-tag--info {
  height: auto;
  white-space: break-spaces;
  word-break: break-all;
}

::v-deep .el-select__tags {
  display: flex;
  flex-wrap: wrap;
}
</style>
